import { queryOptions } from '@tanstack/react-query';
import type { BusinessListFilter } from '../model/list-params';
import type { BusinessKind } from '../lib/business-config';
import { restaurantCategoryApi } from './category-api';
import { restaurantBannerApi } from './restaurant-banner-api';
import { productBannerApi } from './product-banner-api';
import { businessLookupApi } from './lookup-api';
import { cuisineApi } from './cuisine-api';

export const businessKeys = {
  all: ['business'] as const,
  lists: () => [...businessKeys.all, 'list'] as const,
  list: (kind: BusinessKind, filter: BusinessListFilter) =>
    [...businessKeys.lists(), kind, filter] as const,
  listAll: (kind: BusinessKind) => [...businessKeys.lists(), kind, 'all'] as const,
  lookups: () => [...businessKeys.all, 'lookup'] as const,
  cuisines: () => [...businessKeys.all, 'cuisines'] as const,
  restaurantOptions: (name: string) =>
    [...businessKeys.lookups(), 'restaurants', name] as const,
  productOptions: (name: string) =>
    [...businessKeys.lookups(), 'products', name] as const,
};

export const businessQueries = {
  cuisines: () =>
    queryOptions({ queryKey: businessKeys.cuisines(), queryFn: () => cuisineApi.list() }),
  categoryList: (filter: BusinessListFilter) =>
    queryOptions({
      queryKey: businessKeys.list('categories', filter),
      queryFn: () => restaurantCategoryApi.list(filter),
    }),

  categoryAll: () =>
    queryOptions({
      queryKey: businessKeys.listAll('categories'),
      queryFn: () => restaurantCategoryApi.listAll(),
    }),

  restaurantBannerList: (filter: BusinessListFilter) =>
    queryOptions({
      queryKey: businessKeys.list('restaurant-banners', filter),
      queryFn: () => restaurantBannerApi.list(filter),
    }),

  restaurantBannerAll: () =>
    queryOptions({
      queryKey: businessKeys.listAll('restaurant-banners'),
      queryFn: () => restaurantBannerApi.listAll(),
    }),

  productBannerList: (filter: BusinessListFilter) =>
    queryOptions({
      queryKey: businessKeys.list('product-banners', filter),
      queryFn: () => productBannerApi.list(filter),
    }),

  productBannerAll: () =>
    queryOptions({
      queryKey: businessKeys.listAll('product-banners'),
      queryFn: () => productBannerApi.listAll(),
    }),

  restaurantOptions: (name: string) =>
    queryOptions({
      queryKey: businessKeys.restaurantOptions(name),
      queryFn: () => businessLookupApi.searchRestaurants(name),
    }),

  productOptions: (name: string) =>
    queryOptions({
      queryKey: businessKeys.productOptions(name),
      queryFn: () => businessLookupApi.searchProducts(name),
    }),
};
